import {
    InsightCalculateMatrixParamsRequest,
    InsightCalculateMatrixResponse,
    InsightCalculateParamsRequest,
    InsightCalculateParamsResponse,
    InsightGetSymbolFundamentalsRequestModeEnum,
    InsightOrderingDirectionEnum,
    InsightPaging,
    InsightSymbolSocialsResponse,
} from '@orbis-systems/orbis-api-clients/insight';
import { chunk, isEmpty, multiply, uniqBy } from 'lodash-es';
import { inject, injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import { BondQuantityType, CorporateActionsType, MostActiveOrderFilter, PriceType } from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import {
    Apis,
    BondQuote,
    CorporateAction,
    EtfBreakdownEntry,
    MiddlewareConfig,
    MostActive,
    MostActiveWithExtraInfo,
    OptionChain,
    OptionChainDates,
    Quote,
    QuoteFundamentals,
    Shortability,
    TiprankOverviewWithExtraInfo,
} from '../types';
import { formatOptionChain, formatQuote, formatQuoteFundamentals } from '../utils';
import type { TipranksApi } from './TipranksApi';

@injectable()
export class QuoteApi extends BaseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(ApiInjectToken.TipranksApi) protected readonly tipranksApi: TipranksApi,
    ) {
        super(apis, config);
    }

    async getTopTenAssets(): Promise<Quote[]> {
        const response = await this.apis.orbis.research.topStocks('MA', { market: 'BIG3', order: 'H' });

        return (response.data as EtfBreakdownEntry[]).map((item) => item.quote);
    }

    async searchQuotes(criteria: string, limit?: number): Promise<Quote[]> {
        let results: Quote[];
        if (this.apis.aesMidas) {
            results = (
                await this.apis.aesMidas.quotesReformat.getSearchRedis({
                    criteria,
                    limit,
                    loadQuotes: true,
                    includeMF: true,
                })
            ).data;
        } else {
            results = (
                await this.apis.orbis.quotes.search({
                    criteria: criteria.toUpperCase(),
                    limit: limit || 10,
                    loadQuotes: true,
                })
            ).data;
        }
        return results.filter((quote) => !quote.notFound && !quote.notPermissioned).map(formatQuote);
    }

    async searchBondQuotes(criteria: string, limit?: number): Promise<Quote[]> {
        let results: Quote[];
        if (this.apis.aesMidas) {
            results = (await this.apis.aesMidas.quotesReformat.getBondsSearch({ criteria, limit, loadQuotes: true }))
                .data;
        } else {
            results = (
                await this.apis.orbis.quotes.bondsSearch({
                    criteria: criteria.toUpperCase(),
                    limit: limit || 10,
                    loadQuotes: true,
                })
            ).data;
        }

        return results
            .filter((quote) => !quote.notFound && !quote.notPermissioned)
            .map(formatQuote)
            .map((quote) => ({
                ...quote,
                lastPrice: multiply(quote.lastPrice || 0, 100),
            }));
    }

    async getQuoteSet(): Promise<Quote[]> {
        throw new Error('Not supported');
    }

    async getQuotes(symbols: string[], crypto?: boolean): Promise<Quote[]> {
        const maxQuotes = 50; // API limitations
        const responses = await Promise.all(
            chunk(symbols, maxQuotes).map(async (symbolList) => {
                const symbols = symbolList.join(',');
                if (crypto) {
                    return this.apis.orbis.quotes.crypto({ symbols });
                } else if (this.apis.aesMidas) {
                    return this.apis.aesMidas.quotesReformat.getEquity({
                        symbols,
                    });
                } else {
                    return this.apis.orbis.quotes.equity({ symbols });
                }
            }),
        );
        const quotes = responses.flatMap((response) => response.data);
        // Equity endpoint still returns crypto data for some reason, so we filter here to make sure no duplicates
        const filteredData = uniqBy(quotes, (item) => item.symbol);
        return filteredData.filter((quote) => !quote.notFound && !quote.notPermissioned).map(formatQuote);
    }

    async getQuote(symbol: string, crypto?: boolean): Promise<Quote | null> {
        let quotes: Quote[] | null = null;
        if (crypto) {
            quotes = (await this.apis.orbis.quotes.crypto({ symbols: symbol })).data;
        } else if (this.apis.aesMidas) {
            quotes = (await this.apis.aesMidas.quotesReformat.getEquity({ symbols: symbol })).data;
        } else {
            quotes = (await this.apis.orbis.quotes.equity({ symbols: symbol })).data;
        }

        if (quotes) {
            const quote = quotes.find((quote) => quote.symbol === symbol.toUpperCase());
            return quote ? formatQuote(quote) : null;
        } else {
            return null;
        }
    }
    // Orbis API takes multiple ISIN separated by commas, but we are only sending one for now until we can have insight team support same logic
    async getBondQuote(identifiers: string): Promise<BondQuote | null> {
        let quote;

        if (this.apis.aesMidas) {
            quote = (
                await this.apis.aesMidas.quotesReformat.getBonds({
                    identifiers,
                    bondPriceType: PriceType.Percentage,
                    bondQuantityType: BondQuantityType.Unit,
                })
            ).data[0];
        } else {
            quote = (
                await this.apis.orbis.quotes.bonds({
                    isin: identifiers,
                    // @ts-expect-error: // FIXME: These params below are not added in docuementation correctly
                    bondPriceType: PriceType.Percentage,
                    bondQuantityType: BondQuantityType.Unit,
                })
            ).data[0];
        }

        if (!quote || quote.notFound || isEmpty(quote)) {
            return null;
        }

        const insightFixedIncomeInfo = (await this.apis.insight.fixedIncome.getSingleFixedIncomeEntry(identifiers))
            .data;

        quote = { ...quote, ...insightFixedIncomeInfo };

        // Description is a better value for companyName. We call for companyName from Quote in various components so this makes it more consistent
        const formattedBondQuote = {
            companyName: quote.issuer || quote.companyName,
            lastPrice: quote.lastPrice || 0,
            ask: quote.ask || 0,
            bid: quote.bid || 0,
        };

        return { ...quote, ...formattedBondQuote };
    }

    async getOptionQuotes(symbols: string[], greeks: boolean = true): Promise<Quote[]> {
        let data;
        const symbolList = symbols.join(',');

        if (this.apis.aesMidas) {
            data = (await this.apis.aesMidas.quotesReformat.getOption({ symbols: symbolList, greeks })).data;
        } else {
            data = (await this.apis.orbis.quotes.option({ symbols: symbolList, greeks })).data;
        }

        return data;
    }

    async getQuoteOptionChainDates(symbol: string): Promise<OptionChainDates> {
        let data;
        if (this.apis.aesMidas) {
            data = (await this.apis.aesMidas.quotesReformat.getOptionChainDates(symbol)).data;
        } else {
            data = (await this.apis.orbis.quotes.optionChainDates(symbol)).data;
        }
        return data;
    }

    async getQuoteOptionChainQuotes(symbol: string, date: string): Promise<OptionChain> {
        let data;
        if (this.apis.aesMidas) {
            data = (await this.apis.aesMidas.quotesReformat.getOptionChain(symbol, date)).data;
        } else {
            data = (await this.apis.orbis.quotes.optionChain(symbol, date)).data;
        }
        return formatOptionChain(data);
    }

    async getMostActiveStocks({
        order,
        paging,
        volume,
        tipranks,
    }: {
        order?: {
            direction: InsightOrderingDirectionEnum;
            field: MostActiveOrderFilter; // FIXME: will be changed to insight team enum; waiting for backend to update docs
        };
        paging: InsightPaging;
        volume?: number;
        tipranks: boolean;
    }): Promise<MostActiveWithExtraInfo[]> {
        const mostActiveResponse = await this.apis.insight.stockScreener.getTopSymbols({
            order,
            paging,
            exchange: ['NYSE', 'NAS', 'ASE'],
            volume: {
                more_than: volume,
            },
            market_cap: {
                more_than: 0,
            },
        });

        const mostActive = mostActiveResponse.data.details;
        const symbols = mostActive.map((item) => item.symbol);
        //FIXME: adding Daniils quote until insight team will fix their quote data
        const quotes = await this.getQuotes(symbols);

        let tipranksData: TiprankOverviewWithExtraInfo[] = [];
        if (tipranks) {
            try {
                tipranksData = await this.tipranksApi.getTipranksAssetsOverview(symbols);
            } catch {
                tipranksData = [];
            }
        }

        return mostActive.map((mostActiveItem: MostActive) => {
            const currentQuote = quotes.find((quote) => quote.symbol === mostActiveItem.symbol);
            return {
                ...mostActiveItem,
                last_price: currentQuote?.lastPrice || mostActiveItem.last_price,
                price_change: currentQuote?.change || mostActiveItem.price_change,
                price_change_percent: currentQuote?.changePercent || mostActiveItem.price_change_percent,
                tipranks: tipranksData.find((item) => item.ticker === mostActiveItem.symbol),
            };
        });
    }

    async getQuoteShortability(symbol: string): Promise<Shortability> {
        const response = await this.apis.orbis.quotes.shortability(symbol);
        return response.data;
    }

    async getQuoteSocials(symbol: string) {
        const response = await this.apis.insight.logos.logosSocialSymbolLogos({ symbol });

        return response.data as InsightSymbolSocialsResponse;
    }

    async getCorporateActions(
        symbols: string[],
        dateFrom: string,
        dateTo: string,
        types?: CorporateActionsType,
    ): Promise<CorporateAction[]> {
        const actions = (
            await this.apis.orbis.research.corporateActions.search({
                symbol: symbols.join(','),
                dateFrom,
                dateTo,
                type: types,
            })
        ).data as CorporateAction[];

        return actions;
    }

    async getQuoteFundamentals(
        symbol: string,
        mode: InsightGetSymbolFundamentalsRequestModeEnum,
    ): Promise<QuoteFundamentals> {
        const response = await this.apis.insight.fundamentals.getFundamentalsBySymbolData({ symbol, mode });
        return formatQuoteFundamentals(response.data);
    }

    async getQuoteProfile(symbol: string) {
        const response = await this.apis.insight.companyProfile.getCombinedProfile({ symbol });
        return response.data;
    }

    async groupSymbolsByFundType(symbols: string[]) {
        const response = await this.apis.insight.companyProfile.getSymbolsPerSymbolType({ symbols });
        return response.data;
    }

    async getQuotesSnapshot(symbols: string[], userId?: number): Promise<Quote[]> {
        const { data } = await (this.apis.aesMidas
            ? this.apis.aesMidas.quotesReformat.getEquitySnapshot({
                  symbols: symbols.join(', '),
              })
            : this.apis.orbis.orders.v2.equities.previewSnapshot({
                  symbols: symbols.join(', '),
                  // referenceId can be used if you want to track who is sending the requests
                  referenceId: userId,
              }));
        return data;
    }

    async calculateOptionGreeksMatrix(
        params: InsightCalculateMatrixParamsRequest,
    ): Promise<InsightCalculateMatrixResponse> {
        const response = await this.apis.insight.optionGreeks.ogCalculateMatrix(params);
        return response.data;
    }

    async calculateOptionGreeksParams(params: InsightCalculateParamsRequest): Promise<InsightCalculateParamsResponse> {
        const response = await this.apis.insight.optionGreeks.ogCalculateParams(params);
        return response.data;
    }

    async getQuotesFxRate(symbols: string[]): Promise<Quote[]> {
        let data;
        if (this.apis.aesMidas) {
            data = (await this.apis.aesMidas.quotesReformat.getQuotesReformat({ symbols: symbols.join(', ') })).data;
        } else {
            data = (await this.apis.orbis.quotes.fxRate({ symbols })).data;
        }
        return data;
    }
}
